import React, { useEffect, useState } from "react";
import axios from "axios";
import Select from "react-select";
import { API_URL, LOCATION_ID, TOKEN } from "..";
import MasterLayout from "../masterLayout/MasterLayout";
import { Icon } from "@iconify/react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {Link} from 'react-router-dom'

const ReportingPage = () => {
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const formatDate = (date) => date.toISOString().split("T")[0];

  const [fromDate, setFromDate] = useState(formatDate(firstDayOfMonth));
  const [toDate, setToDate] = useState(formatDate(today));
  const [accounts, setAccounts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState({
    value: "all",
    label: "All Accounts",
  });
  const [selectedCustomer, setSelectedCustomer] = useState({
    value: "all",
    label: "All Customers",
  });
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  // ✅ Fetch Accounts
  const fetchAccounts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/accounts/location/${LOCATION_ID}`);
      const data = await res.json();
      const accountOptions = [
        { value: "all", label: "All Accounts" },
        ...data.accounts.map((a) => ({
          value: a.id,
          label: a.accountTitle + `(${a.type})`,
        })),
      ];
      setAccounts(accountOptions);
    } catch (err) {
      console.error("Fetch accounts error:", err);
    }
  };

  // ✅ Fetch Customers from GoHighLevel
  const fetchCustomers = async () => {
    try {
      const res = await axios.get("https://services.leadconnectorhq.com/contacts/", {
        headers: {
          Authorization: `Bearer ${TOKEN}`,
          Version: "2021-07-28",
          Accept: "application/json",
        },
        params: {
          locationId: LOCATION_ID,
          limit: 100,
          page: 1,
        },
      });

      const contactOptions = [
        { value: "all", label: "All Customers" },
        ...(res.data.contacts || []).map((c) => ({
          value: c.id,
          label: `${c.firstName || ""} ${c.lastName || ""}`.trim() || "N/A",
        })),
      ];

      setCustomers(contactOptions);
    } catch (err) {
      console.error("Error fetching customers:", err.message);
    }
  };

  // ✅ Fetch Report Data
  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/payments/overall`, {
        params: {
          fromDate,
          toDate,
          locationId:LOCATION_ID,
          accountId: selectedAccount.value,
          customerId: selectedCustomer.value,
        },
      });
      setReportData(res.data);
    } catch (err) {
      console.error("Error fetching report:", err.message);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Export to Excel
  const exportToExcel = () => {
    if (!reportData) return;

    const tableData = reportData.invoices.map((inv) => ({
      "Invoice #": inv.invoiceNumber,
      "Issue Date": inv.issueDate,
      "Due Date": inv.dueDate,
      "Invoice Amount": inv.invoiceAmount,
      Paid: inv.paidAmount,
      Remaining: inv.remaining,
      Payments: inv.payments
        .map(
          (p) =>
            `${inv.currency}${p.receivedAmount} via ${p.account?.type || "N/A"} (${
              p.account?.title || "N/A"
            })`
        )
        .join(", "),
    }));

    tableData.push({
      "Invoice #": "Totals",
      "Invoice Amount": reportData.stats?.totalInvoiceAmount || 0,
      Paid: reportData.stats?.totalPaidAmount || 0,
      Remaining: reportData.stats?.totalRemainingBalance || 0,
    });

    const worksheet = XLSX.utils.json_to_sheet(tableData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), "report.xlsx");
  };

  // ✅ Export to PDF
  const exportToPDF = () => {
    if (!reportData) return;
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Overall Report", 14, 20);

    const tableData = reportData.invoices.map((inv) => [
      inv.invoiceNumber,
      inv.issueDate,
      inv.dueDate,
      `${inv.currency}${inv.invoiceAmount?.toFixed(2)}`,
      `${inv.currency}${inv.paidAmount?.toFixed(2)}`,
      `${inv.currency}${inv.remaining?.toFixed(2)}`,
      inv.payments.length > 0
        ? inv.payments.map(
            (p) =>
              `${inv.currency}${p.receivedAmount} via ${p.account?.type || "N/A"} (${
                p.account?.title || "N/A"
              })`
          )
        : "No Payments",
    ]);

    tableData.push([
      "Totals",
      "",
      "",
      reportData?.stats?.totalInvoiceAmount?.toFixed(2) || 0,
      reportData?.stats?.totalPaidAmount?.toFixed(2) || 0,
      reportData?.stats?.totalRemainingBalance?.toFixed(2) || 0,
      "-",
    ]);

    autoTable(doc, {
      startY: 30,
      head: [["Invoice #", "Issue Date", "Due Date", "Amount", "Paid", "Remaining", "Payments"]],
      body: tableData,
      theme: "striped",
      headStyles: { fillColor: [41, 128, 185] },
    });

    doc.save("report.pdf");
  };

  useEffect(() => {
    fetchAccounts();
    fetchCustomers();
    fetchReport();
  }, []);

  return (
    <MasterLayout>
      <div className="container py-5">
        <h3 className="mb-4">Reporting</h3>

        {/* Filters */}
        <div className="mb-3">
            <div className="row">
                <div className="col-md-2">
                <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="form-control"
                />
                </div>
                <div className="col-md-2">
                <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="form-control"
          />
                </div>
                <div className="col-md-3">
                <Select
              options={accounts}
              value={selectedAccount}
              onChange={setSelectedAccount}
            />
                </div>
                <div className="col-md-3">
                <Select
              options={customers}
              value={selectedCustomer}
              onChange={setSelectedCustomer}
            />
                </div>
                <div className="col-md-2">
                <button className="btn btn-primary" onClick={fetchReport}>
            Filter
          </button>
                </div>
            </div>
        
       
        
        </div>

        {/* Export Buttons */}
        <div className="mb-3">
          <button className="btn btn-outline-primary me-2" onClick={exportToPDF}>
            <Icon icon="mdi:printer" className="me-1" /> PDF
          </button>
          <button className="btn btn-outline-success" onClick={exportToExcel}>
            <Icon icon="mdi:download" className="me-1" /> Excel
          </button>
        </div>

        {/* Table */}
        <div className="card shadow-sm border-0">
          <div className="card-body">
            {loading ? (
              <p>Loading report...</p>
            ) : reportData && reportData.invoices.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-bordered table-striped text-center">
                  <thead className="table-primary">
                    <tr>
                      <th>Invoice #</th>
                      <th>Issue Date</th>
                      <th>Due Date</th>
                      <th>Amount</th>
                      <th>Paid</th>
                      <th>Remaining</th>
                      <th>Payments</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.invoices.map((inv) => (
                      <tr key={inv.id}>
                        <td><Link className="text-primary" to={`/invoice-preview/${inv.id}`}>{inv.invoiceNumber}</Link></td>
                        <td>{inv.issueDate} </td>
                        <td>{inv.dueDate}</td>
                        <td>{inv.currency}{inv.invoiceAmount?.toFixed(2)}</td>
                        <td className="text-success fw-semibold">
                          {inv.currency}{inv.paidAmount?.toFixed(2)}
                        </td>
                        <td className={inv.remaining > 0 ? "text-danger fw-semibold" : "text-muted"}>
                          {inv.currency}{inv.remaining?.toFixed(2)}
                        </td>
                        <td>
                          {inv.payments.length > 0 ? (
                            <ul className="list-unstyled mb-0">
                              {inv.payments.map((p) => (
                                <li key={p.id}>
                                  {inv.currency}{p.receivedAmount} via {p.account?.type || "N/A"} (
                                  {p.account?.title || "N/A"})
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <span className="badge bg-secondary">No Payments</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="table-dark fw-bold">
                    <tr>
                      <td colSpan="3" className="text-end">Totals:</td>
                      <td>{reportData?.stats?.totalInvoiceAmount?.toFixed(2) || 0}</td>
                      <td>{reportData?.stats?.totalPaidAmount?.toFixed(2) || 0}</td>
                      <td>{reportData?.stats?.totalRemainingBalance?.toFixed(2) || 0}</td>
                      <td>-</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <p>No report data found.</p>
            )}
          </div>
        </div>
      </div>
    </MasterLayout>
  );
};

export default ReportingPage;
